// =============================================================================
// src/lib/userRoles.js
// Single source of truth for user role values across the PracticeOS frontend.
// Mirrors the public.user_role Postgres enum + adds UI metadata (label,
// category, color, capability flags).
//
// THE DATABASE IS THE CONSTRAINT. This module is the frontend's mirror of the
// enum plus the metadata needed for dropdowns, badges, and capability checks.
// If a value here doesn't exist in the DB enum, INSERT/UPDATE will fail at
// write time - that's the safety net. Never add a value to USER_ROLES without
// also adding it to the enum via migration.
//
// Capability flags replace the dozens of inline `["Owner","Manager"].includes`
// checks scattered across the codebase. Use isAdminRole / isSupervisorRole /
// isCareTeamRole / isClinicalBillingRole instead.
//
// "Care Manager Supervisor" is intentionally NOT in this list - it's a
// deprecated enum value with zero rows in production. New code should always
// write "Supervising Care Manager".
// =============================================================================

export const USER_ROLES = [
  { key: "Owner",                    label: "Owner",                    category: "admin",     color: "#6D28D9", is_admin: true,  is_supervisor: true,  is_clinical_billing: false, is_care_team: false, is_staff: true,  sort_order: 10  },
  { key: "Manager",                  label: "Manager",                  category: "admin",     color: "#1D4ED8", is_admin: true,  is_supervisor: true,  is_clinical_billing: false, is_care_team: false, is_staff: true,  sort_order: 20  },
  { key: "Provider",                 label: "Provider",                 category: "clinical",  color: "#0F6E56", is_admin: false, is_supervisor: false, is_clinical_billing: true,  is_care_team: false, is_staff: true,  sort_order: 30  },
  { key: "Medical Assistant",        label: "Medical Assistant",        category: "clinical",  color: "#D08A2E", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: false, is_staff: true,  sort_order: 40  },
  { key: "RN",                       label: "Registered Nurse",         category: "clinical",  color: "#0F6E56", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: false, is_staff: true,  sort_order: 50  },
  { key: "LPN",                      label: "Licensed Practical Nurse", category: "clinical",  color: "#0F6E56", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: false, is_staff: true,  sort_order: 60  },
  { key: "Pharmacist",               label: "Pharmacist",               category: "clinical",  color: "#0F6E56", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: false, is_staff: true,  sort_order: 70  },
  { key: "Front Desk",               label: "Front Desk",               category: "support",   color: "#0E7490", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: false, is_staff: true,  sort_order: 80  },
  { key: "Billing",                  label: "Billing",                  category: "support",   color: "#991B1B", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: false, is_staff: true,  sort_order: 90  },
  { key: "Care Manager",             label: "Care Manager",             category: "care_team", color: "#7C3AED", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: true,  is_staff: true,  sort_order: 100 },
  { key: "Supervising Care Manager", label: "Supervising Care Manager", category: "care_team", color: "#7C3AED", is_admin: false, is_supervisor: true,  is_clinical_billing: false, is_care_team: true,  is_staff: true,  sort_order: 110 },
  { key: "CHW",                      label: "Community Health Worker",  category: "care_team", color: "#0E7490", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: true,  is_staff: true,  sort_order: 120 },
  { key: "Health Educator",          label: "Health Educator",          category: "care_team", color: "#0E7490", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: true,  is_staff: true,  sort_order: 130 },
  { key: "System",                   label: "System",                   category: "system",    color: "#6B7280", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: false, is_staff: false, sort_order: 950 },
  { key: "Patient",                  label: "Patient",                  category: "patient",   color: "#9C9B94", is_admin: false, is_supervisor: false, is_clinical_billing: false, is_care_team: false, is_staff: false, sort_order: 900 },
];

// Lookup map for O(1) role-key -> metadata
export const ROLES_BY_KEY = Object.fromEntries(USER_ROLES.map((r) => [r.key, r]));

// -----------------------------------------------------------------------------
// COMMON SUBSETS (string arrays, suitable for dropdowns and `.includes()` checks)
// These replace dozens of inline arrays scattered across the codebase.
// -----------------------------------------------------------------------------

// Roles available in staff-add UI (excludes Patient - portal accounts only)
export const STAFF_ROLES = USER_ROLES.filter((r) => r.is_staff).map((r) => r.key);

// Admin roles (Owner, Manager) - used for elevated-permission checks
export const ADMIN_ROLES = USER_ROLES.filter((r) => r.is_admin).map((r) => r.key);

// Supervisor roles (admins + Supervising Care Manager) - care management
// supervision checks
export const SUPERVISOR_ROLES = USER_ROLES.filter((r) => r.is_supervisor).map((r) => r.key);

// Care team roles (CMs, CHW, Health Educator) - Care Schedule eligibility
export const CARE_TEAM_ROLES = USER_ROLES.filter((r) => r.is_care_team).map((r) => r.key);

// Clinical billing roles (Provider only, currently) - rendering encounters
export const CLINICAL_BILLING_ROLES = USER_ROLES.filter((r) => r.is_clinical_billing).map((r) => r.key);

// -----------------------------------------------------------------------------
// CATEGORY-GROUPED for picker UIs that want section labels
// -----------------------------------------------------------------------------
export const STAFF_ROLES_BY_CATEGORY = {
  admin:     USER_ROLES.filter((r) => r.is_staff && r.category === "admin"),
  clinical:  USER_ROLES.filter((r) => r.is_staff && r.category === "clinical"),
  support:   USER_ROLES.filter((r) => r.is_staff && r.category === "support"),
  care_team: USER_ROLES.filter((r) => r.is_staff && r.category === "care_team"),
};

// -----------------------------------------------------------------------------
// CAPABILITY PREDICATES - prefer these over `.includes()` checks
// -----------------------------------------------------------------------------
function roleHas(role, capability) {
  const meta = ROLES_BY_KEY[role];
  return meta ? !!meta[capability] : false;
}

export const isAdminRole            = (r) => roleHas(r, "is_admin");
export const isSupervisorRole       = (r) => roleHas(r, "is_supervisor");
export const isCareTeamRole         = (r) => roleHas(r, "is_care_team");
export const isClinicalBillingRole  = (r) => roleHas(r, "is_clinical_billing");
export const isStaffRole            = (r) => roleHas(r, "is_staff");

// -----------------------------------------------------------------------------
// Role label (display) lookup with fallback to the key itself
// -----------------------------------------------------------------------------
export function roleLabel(role) {
  return ROLES_BY_KEY[role]?.label || role;
}

// -----------------------------------------------------------------------------
// Role color lookup for badges/avatars (fallback to neutral gray)
// -----------------------------------------------------------------------------
export function roleColor(role) {
  return ROLES_BY_KEY[role]?.color || "#9C9B94";
}
